import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import ImageUpload from '../../components/ImageUpload';

const initialBlog = {
  title: '',
  content: '',
  author: '',
  images: [],
  excerpt: '',
  date: ''
};

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blog, setBlog] = useState(initialBlog);
  const [search, setSearch] = useState('');
  const [imageUrls, setImageUrls] = useState([]);

  // Fetch blogs
  useEffect(() => {
    fetch(getApiUrl('/api/admin/blogs'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setBlogs(Array.isArray(data) ? data : []));
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBlog(prev => ({ ...prev, [name]: value }));
  };

  // Handle image upload
  const handleImagesChange = (urls) => {
    setImageUrls(urls);
  };

  // Handle edit
  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setBlog({
      ...blog,
      date: blog.date ? new Date(blog.date).toISOString().split('T')[0] : ''
    });
    // Ensure initialImages are in the expected format (objects with url, type, publicId)
    setImageUrls(blog.images && Array.isArray(blog.images) 
      ? blog.images.map(img => typeof img === 'string' ? { url: img, type: 'image', publicId: null } : img)
      : []
    );
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Map imageUrls to just their URLs before sending to backend
    const imagesToSend = imageUrls.filter(img => img && img.url).map(img => img.url);
    const blogData = { ...blog, images: imagesToSend };

    try {
      const url = editingBlog 
        ? getApiUrl(`/api/admin/blogs/${editingBlog._id}`)
        : getApiUrl('/api/admin/blogs');
      
      const response = await fetch(url, {
        method: editingBlog ? 'PUT' : 'POST',
        headers: {
          'x-admin': 'true',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(blogData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingBlog ? 'update' : 'create'} blog`);
      }
      
      const newBlog = await response.json();
      if (editingBlog) {
        setBlogs(prev => prev.map(b => b._id === editingBlog._id ? newBlog : b));
      } else {
      setBlogs(prev => [...prev, newBlog]);
      }
      
      setShowModal(false);
      setBlog(initialBlog);
      setImageUrls([]);
      setEditingBlog(null);
    } catch (error) {
      console.error('Error saving blog:', error);
      alert(`Failed to ${editingBlog ? 'update' : 'create'} blog. Please try again.`);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Blogs</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Blog
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search blogs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
        </div>
      </div>

      {/* Blogs table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {blogs.map((blog) => (
              <tr key={blog._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {blog.images && blog.images.length > 0 ? (
                    <img
                      src={blog.images[0].url || blog.images[0]} // Access url property for image display
                      alt={blog.title}
                      className="h-12 w-12 object-cover rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded"></div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{blog.title}</td>
                <td className="px-6 py-4 whitespace-nowrap">{blog.author}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button className="text-blue-500 hover:text-blue-700 mr-2" onClick={() => handleEdit(blog)}>
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button className="text-red-500 hover:text-red-700">
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Blog Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingBlog ? 'Edit Blog' : 'Add New Blog'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Images</label>
                  <ImageUpload
                    onImagesChange={handleImagesChange}
                    multiple={true}
                    maxFiles={5}
                    accept="image/*"
                    className="mt-1"
                    initialImages={imageUrls}
                    returnMeta={true}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={blog.title}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Content</label>
                  <textarea
                    name="content"
                    value={blog.content}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Author</label>
                  <input
                    type="text"
                    name="author"
                    value={blog.author}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Excerpt</label>
                  <textarea
                    name="excerpt"
                    value={blog.excerpt}
                    onChange={handleChange}
                    required
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={blog.date}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBlog(null);
                    setBlog(initialBlog);
                    setImageUrls([]);
                  }}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingBlog ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blogs; 